import React, { useState, useEffect, useCallback } from 'react';
import AddUserModal from './AddUserModal';
import EditUserModal from './EditUserModal';
import { getUsers, deleteUser } from '../../services/api';
import './UserManagement.css';

const PAGE_SIZES = [5, 10, 20];

function SortIcon({ column, sortField, sortDir }) {
  if (sortField !== column) {
    return (
      <svg className="sort-icon neutral" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="7 15 12 20 17 15" opacity="0.4" />
        <polyline points="7 9 12 4 17 9" opacity="0.4" />
      </svg>
    );
  }
  return sortDir === 'asc' ? (
    <svg className="sort-icon active" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="7 9 12 4 17 9" />
    </svg>
  ) : (
    <svg className="sort-icon active" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="7 15 12 20 17 15" />
    </svg>
  );
}

const avatarColors = ['#1d4ed8', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];

// Extract numeric suffix from "USER-n" for consistent colour assignment
const getAvatarColor = (id) => {
  const num = id && id.startsWith('USER-') ? parseInt(id.slice(5), 10) : 0;
  return avatarColors[(isNaN(num) ? 0 : num) % avatarColors.length];
};
const getInitials = (name) => name.slice(0, 2).toUpperCase();

const columns = [
  { key: 'id',        label: 'ID',         sortable: true  },
  { key: 'username',  label: 'Username',   sortable: true  },
  { key: 'email',     label: 'Email',      sortable: true  },
  { key: 'role',      label: 'Role',       sortable: true  },
  { key: 'enabled',   label: 'Status',     sortable: true  },
  { key: 'createdBy', label: 'Created By', sortable: false },
  { key: 'updatedBy', label: 'Updated By', sortable: false },
  { key: 'createdAt', label: 'Created At', sortable: true  },
  { key: 'actions',   label: 'Actions',    sortable: false },
];

export default function UserManagement() {
  // ── Server data ──────────────────────────────────────────────────────────────
  const [users, setUsers]               = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages]     = useState(1);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);

  // ── Filter / sort / page state ───────────────────────────────────────────────
  const [search, setSearch]             = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [roleFilter, setRoleFilter]     = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [sortField, setSortField]       = useState('id');
  const [sortDir, setSortDir]           = useState('asc');
  const [page, setPage]                 = useState(1);   // 1-indexed for UI
  const [pageSize, setPageSize]         = useState(5);

  // ── UI state ─────────────────────────────────────────────────────────────────
  const [showModal, setShowModal]       = useState(false);
  const [editUser, setEditUser]         = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);  // user pending deletion
  const [deleting, setDeleting]         = useState(false);
  const [toast, setToast]               = useState(null);

  // ── Debounce search input ────────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  // ── Fetch users ──────────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: page - 1,   // Spring is 0-indexed
        size: pageSize,
        sort: `${sortField},${sortDir}`,
      };
      if (debouncedSearch)             params.search  = debouncedSearch;
      if (roleFilter !== 'ALL')        params.role    = roleFilter;
      if (statusFilter === 'ACTIVE')   params.enabled = true;
      if (statusFilter === 'INACTIVE') params.enabled = false;

      const res = await getUsers(params);
      setUsers(res.data.content);
      setTotalElements(res.data.totalElements);
      setTotalPages(Math.max(1, res.data.totalPages));
    } catch (err) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortField, sortDir, debouncedSearch, roleFilter, statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSearch       = (val) => setSearch(val);
  const handleRoleFilter   = (val) => { setRoleFilter(val);       setPage(1); };
  const handleStatusFilter = (val) => { setStatusFilter(val);     setPage(1); };
  const handlePageSize     = (val) => { setPageSize(Number(val)); setPage(1); };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  const handleAddUser = (serverUser) => {
    setShowModal(false);
    showToast(`User "${serverUser.username}" created successfully`);
    fetchUsers();
  };

  const handleEditSave = (serverUser) => {
    setEditUser(null);
    showToast(`User "${serverUser.username}" updated successfully`);
    fetchUsers();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await deleteUser(deleteConfirm.id);
      showToast(`User "${deleteConfirm.username}" deleted successfully`);
      setDeleteConfirm(null);
      fetchUsers();
    } catch (err) {
      showToast(err.message || 'Failed to delete user', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ── Pagination helpers ───────────────────────────────────────────────────────
  const pageNumbers = () => {
    const pages = [];
    const delta = 2;
    const left  = Math.max(1, page - delta);
    const right = Math.min(totalPages, page + delta);
    if (left > 1) { pages.push(1); if (left > 2) pages.push('...'); }
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages) { if (right < totalPages - 1) pages.push('...'); pages.push(totalPages); }
    return pages;
  };

  const pageStart = totalElements === 0 ? 0 : (page - 1) * pageSize + 1;
  const pageEnd   = Math.min(page * pageSize, totalElements);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="um-wrapper">
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          {toast.type === 'success' ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
          {toast.msg}
        </div>
      )}

      {/* Toolbar */}
      <div className="um-toolbar">
        <div className="um-search-wrap">
          <svg className="um-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className="um-search"
            type="text"
            placeholder="Search by username or email..."
            value={search}
            onChange={e => handleSearch(e.target.value)}
          />
          {search && (
            <button className="um-search-clear" onClick={() => handleSearch('')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        <div className="um-filters">
          <select className="um-select" value={roleFilter} onChange={e => handleRoleFilter(e.target.value)}>
            <option value="ALL">All Roles</option>
            <option value="ADMIN">Admin</option>
            <option value="MANAGER">Manager</option>
            <option value="USER">User</option>
          </select>
          <select className="um-select" value={statusFilter} onChange={e => handleStatusFilter(e.target.value)}>
            <option value="ALL">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
        </div>

        <button className="btn btn-primary um-add-btn" onClick={() => setShowModal(true)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add User
        </button>
      </div>

      {/* Summary */}
      <div className="um-summary">
        {loading ? (
          <span>Loading…</span>
        ) : error ? (
          <span className="um-error-inline">{error}</span>
        ) : (
          <>
            Showing <strong>{pageStart}–{pageEnd}</strong> of <strong>{totalElements}</strong> users
            {(debouncedSearch || roleFilter !== 'ALL' || statusFilter !== 'ALL') && (
              <span className="um-filtered-note"> (filtered)</span>
            )}
          </>
        )}
      </div>

      {/* Table */}
      <div className="um-table-wrap">
        {loading && <div className="um-loading-overlay"><div className="um-spinner" /></div>}
        <table className="um-table">
          <thead>
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  className={col.sortable ? 'sortable' : ''}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                >
                  <div className="th-inner">
                    {col.label}
                    {col.sortable && <SortIcon column={col.key} sortField={sortField} sortDir={sortDir} />}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && users.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="um-empty">
                  <div className="um-empty-inner">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <span>No users found</span>
                    <small>Try adjusting your search or filters</small>
                  </div>
                </td>
              </tr>
            ) : (
              users.map(user => (
                <tr key={user.id}>
                  <td className="td-id">{user.id}</td>
                  <td>
                    <div className="td-user">
                      <div className="user-avatar" style={{ background: getAvatarColor(user.id) }}>
                        {getInitials(user.username)}
                      </div>
                      <span className="user-name">{user.username}</span>
                    </div>
                  </td>
                  <td className="td-email">{user.email}</td>
                  <td>
                    <span className={`badge badge-role ${
                      user.role === 'ADMIN'   ? 'badge-admin'   :
                      user.role === 'MANAGER' ? 'badge-manager' : 'badge-user'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.enabled ? 'active' : 'inactive'}`}>
                      <span className="status-dot" />
                      {user.enabled ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="td-audit">{user.createdBy || '—'}</td>
                  <td className="td-audit">{user.updatedBy || '—'}</td>
                  <td className="td-date">
                    {user.createdAt ? user.createdAt.slice(0, 10) : '—'}
                  </td>
                  <td>
                    <div className="td-actions">
                      <button className="action-btn action-edit" title="Edit user" onClick={() => setEditUser(user)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button className="action-btn action-delete" title="Delete user" onClick={() => setDeleteConfirm(user)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                          <path d="M10 11v6" />
                          <path d="M14 11v6" />
                          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="um-pagination">
        <div className="pg-left">
          <span className="pg-label">Rows per page:</span>
          <select className="pg-select" value={pageSize} onChange={e => handlePageSize(e.target.value)}>
            {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="pg-right">
          <button className="pg-btn" disabled={page === 1} onClick={() => setPage(1)} title="First page">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="11 17 6 12 11 7" />
              <polyline points="18 17 13 12 18 7" />
            </svg>
          </button>
          <button className="pg-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)} title="Previous page">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          {pageNumbers().map((p, i) =>
            p === '...' ? (
              <span key={i} className="pg-ellipsis">...</span>
            ) : (
              <button
                key={i}
                className={`pg-num ${page === p ? 'active' : ''}`}
                onClick={() => setPage(p)}
              >
                {p}
              </button>
            )
          )}

          <button className="pg-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} title="Next page">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
          <button className="pg-btn" disabled={page === totalPages} onClick={() => setPage(totalPages)} title="Last page">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="13 17 18 12 13 7" />
              <polyline points="6 17 11 12 6 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="confirm-overlay" onClick={() => !deleting && setDeleteConfirm(null)}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="confirm-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6M14 11v6" />
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
              </svg>
            </div>
            <h3 className="confirm-title">Delete User</h3>
            <p className="confirm-body">
              Are you sure you want to delete <strong>{deleteConfirm.username}</strong>{' '}
              (<code>{deleteConfirm.id}</code>)?
              This will soft-delete the record — it won't appear in any lists.
            </p>
            <div className="confirm-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? <><span className="btn-spinner" /> Deleting…</> : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && <AddUserModal onClose={() => setShowModal(false)} onSave={handleAddUser} />}
      {editUser  && <EditUserModal user={editUser} onClose={() => setEditUser(null)} onSave={handleEditSave} />}
    </div>
  );
}
